import React, { useState, ReactNode, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTenants, Tenant, TenantSettings } from "@/hooks/useTenants";
import { useServices, Service } from "@/hooks/useServices";
import { useCategories } from "@/hooks/useCategories";
import { useTeamMembers, TeamMember } from "@/hooks/useTeamMembers";
import { useResources, Resource } from "@/hooks/useResources";
import { useCustomers, Customer } from "@/hooks/useCustomers";
import { useTranslation } from 'react-i18next';
import { TenantContext } from "./tenantContextTypes";
import { useTenantURLSync } from "@/hooks/useTenantURLSync";

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { tenants, isLoading: tenantsLoading, hasCheckedTenants, loadError: tenantsLoadError, refetch: retryTenants, updateTenantSettings: updateTenantSettingsHook, updateTenantLanguage: updateTenantLanguageHook, updateTenantName: updateTenantNameHook, updateTenantLogo: updateTenantLogoHook, updateTenantPrimaryColor: updateTenantPrimaryColorHook, updateTenantAddress: updateTenantAddressHook, updateTenantEmail: updateTenantEmailHook, updateTenantGoogleProfile: updateTenantGoogleProfileHook, updateTenantGBPConnection: updateTenantGBPConnectionHook, updateBookingNotice: updateBookingNoticeHook } = useTenants();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const { i18n } = useTranslation();

  // Tenant URL sync hook
  const { updateTenantInURL } = useTenantURLSync(tenants, currentTenant, setCurrentTenant);

  // Wrapped setCurrentTenant that also updates URL
  const setCurrentTenantWithURL = useCallback((tenant: Tenant) => {
    setCurrentTenant(tenant);
    updateTenantInURL(tenant);
  }, [updateTenantInURL]);

  // Use the new hooks for real data
  const {
    services,
    allServices,
    isLoading: servicesLoading,
    createService,
    updateService,
    deleteService
  } = useServices(currentTenant?.id || null);

  const {
    categories,
    isLoading: _categoriesLoading
  } = useCategories(currentTenant?.id || null);

  const {
    teamMembers,
    isLoading: teamMembersLoading,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    pauseTeamMember,
    resumeTeamMember
  } = useTeamMembers(currentTenant?.id || null);
  
  const { 
    resources,
    isLoading: resourcesLoading,
    createResource, 
    updateResource, 
    deleteResource 
  } = useResources(currentTenant?.id || null);
  
  // Bewusst ohne Kundenliste: der Provider umschliesst die ganze App, also
  // wuerde die Liste auf jeder Seite geladen. Gebraucht werden hier nur die
  // Schreiboperationen. Wer die Liste braucht, holt sie sich selbst ueber
  // useCustomers -- react-query teilt sich den Cache unter demselben Schluessel.
  const {
    createCustomer,
    updateCustomer
  } = useCustomers(currentTenant?.id || null, { includeList: false });

  // Die Terminliste lag frueher hier und wurde damit bei *jedem* Seitenaufruf
  // geladen: alle 14.900 Termine des Salons seit Anbeginn, 2,7 MB Rohdaten,
  // ohne Datumsfilter -- und die abgesagten wurden erst im Browser
  // aussortiert. Verbraucht hat sie nur components/calendar/Calendar.tsx, das
  // seine Daten jetzt selbst holt. Die eigentliche Kalenderansicht
  // (CalendarView) laedt ohnehin nur den sichtbaren Zeitraum.

  // Compute unified initialization state - only wait for critical data
  const isInitializing = authLoading ||
    tenantsLoading ||
    !hasCheckedTenants ||
    (!currentTenant && tenants.length > 0) ||
    Boolean(currentTenant && (servicesLoading || teamMembersLoading)); // Only wait for critical data

  // Separate state for non-critical data
  const isLoadingNonCritical = resourcesLoading;

  // Set first tenant as current immediately when tenants load
  useEffect(() => {
    if (tenants.length > 0 && !currentTenant) {
      // Initializing tenant selection
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentTenant(prev => prev ? prev : tenants[0]);
    }
  }, [tenants, currentTenant]); // Include currentTenant dependency

  // Update language when current tenant changes
  useEffect(() => {
    if (currentTenant?.language && i18n.language !== currentTenant.language) {
      i18n.changeLanguage(currentTenant.language);
    }
  }, [currentTenant?.language, i18n]);

  // Reset tenant when user logs out
  useEffect(() => {
    if (!user) {
      // Clearing tenant state on logout
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentTenant(prev => prev ? null : prev);
    }
  }, [user]);

  const addService = async (service: Omit<Service, 'id' | 'created_at' | 'updated_at' | 'deleted_at' | 'appointment_type_categories'>) => {
    const result = await createService(service);
    if (result.error) {
      throw result.error;
    }
    return result.data;
  };

  const addTeamMember = async (teamMember: Omit<TeamMember, 'id' | 'created_at' | 'updated_at'>) => {
    const result = await createTeamMember(teamMember);
    if (result.error) {
      throw result.error;
    }
    return { id: result.data!.id };
  };

  const addResource = async (resource: Omit<Resource, 'id' | 'created_at' | 'updated_at'>) => {
    await createResource(resource);
  };

  const addCustomer = async (customer: Omit<Customer, 'id' | 'created_at' | 'updated_at'>): Promise<{ data: Customer | null; error: Error | null }> => {
    const result = await createCustomer(customer);
    return {
      data: result.data,
      error: result.error instanceof Error ? result.error : (result.error ? new Error(String(result.error)) : null)
    };
  };

  return (
    <TenantContext.Provider value={{
      currentTenant,
      tenants,
      services,
      allServices,
      categories,
      teamMembers,
      resources,
      isInitializing,
      isLoadingNonCritical,
      tenantsLoadError,
      retryTenants,
      setCurrentTenant: setCurrentTenantWithURL,
      addService,
      updateService: async (id: string, updates: Partial<Service>) => {
        const result = await updateService(id, updates);
        if (result.error) {
          throw result.error;
        }
        return result.data;
      },
      deleteService: async (id: string) => {
        await deleteService(id);
      },
      addTeamMember,
      updateTeamMember: async (id: string, updates: Partial<TeamMember>) => {
        await updateTeamMember(id, updates);
      },
      deleteTeamMember: async (id: string) => {
        await deleteTeamMember(id);
      },
      pauseTeamMember: async (id: string, until?: string | null, reason?: string | null) => {
        await pauseTeamMember(id, until, reason);
      },
      resumeTeamMember: async (id: string) => {
        await resumeTeamMember(id);
      },
      addResource,
      updateResource: async (id: string, updates: Partial<Resource>) => {
        await updateResource(id, updates);
      },
      deleteResource: async (id: string) => {
        await deleteResource(id);
      },
      addCustomer,
      updateCustomer: async (id: string, updates: Partial<Customer>) => {
        await updateCustomer(id, updates);
      },
      updateTenantSettings: async (settings: TenantSettings) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantSettingsHook(currentTenant.id, settings);
        // Update the current tenant with new settings
        setCurrentTenant(prev => prev ? { ...prev, settings } : null);
      },
      updateTenantLanguage: async (language: string) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantLanguageHook(currentTenant.id, language);
        // Update the current tenant with new language
        setCurrentTenant(prev => prev ? { ...prev, language } : null);
      },
      updateTenantName: async (name: string) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantNameHook(currentTenant.id, name);
        // Update the current tenant with new name
        setCurrentTenant(prev => prev ? { ...prev, name } : null);
      },
      updateTenantLogo: async (logoUrl: string) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantLogoHook(currentTenant.id, logoUrl);
        // Update the current tenant with new logo
        setCurrentTenant(prev => prev ? { ...prev, logo_url: logoUrl } : null);
      },
      updateTenantPrimaryColor: async (primaryColor: string) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantPrimaryColorHook(currentTenant.id, primaryColor);
        // Update the current tenant with new primary color
        setCurrentTenant(prev => prev ? { ...prev, primary_color: primaryColor } : null);
      },
      updateTenantAddress: async (address: string, city?: string) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantAddressHook(currentTenant.id, address, city);
        // Update the current tenant with new address
        setCurrentTenant(prev => prev ? { ...prev, address, city: city ?? null } : null);
      },
      updateTenantEmail: async (email: string) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantEmailHook(currentTenant.id, email);
        // Update the current tenant with new email
        setCurrentTenant(prev => prev ? { ...prev, email } : null);
      },
      updateTenantGoogleProfile: async (googleData: {
        google_place_id: string;
        google_cid?: string;
        google_maps_review_url?: string;
        location_coordinate?: string;
      }) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantGoogleProfileHook(currentTenant.id, googleData);
        // Update the current tenant with new Google profile data
        setCurrentTenant(prev => prev ? { ...prev, ...googleData } : null);
      },
      updateTenantGBPConnection: async (gbpData: {
        gbp_access_token: string | null;
        gbp_refresh_token: string | null;
        gbp_token_expires_at: string | null;
        gbp_account_id: string | null;
        gbp_location_id: string | null;
        gbp_location_name: string | null;
        gbp_connected_at: string | null;
      }) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateTenantGBPConnectionHook(currentTenant.id, gbpData);
        setCurrentTenant(prev => prev ? { ...prev, ...gbpData } : null);
      },
      updateBookingNotice: async (enabled: boolean, text: string | null) => {
        if (!currentTenant) throw new Error('No current tenant');
        await updateBookingNoticeHook(currentTenant.id, enabled, text);
        setCurrentTenant(prev => prev ? { ...prev, booking_notice_enabled: enabled, booking_notice_text: text } : null);
      }
    }}>
      {children}
    </TenantContext.Provider>
  );
};
